/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 122 lines
 * - Adding: ~4 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    INVENTORY_PAGE_SPLIT_HOOK_ACTIONS
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Extracted actions from InventoryPage.tsx.
 */

import React, { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getInventoryItems, deleteInventoryItem } from "../../../../services/inventoryService";
import { notify, getErrorMessage } from "../../../../utils/notifications";
import { InventoryState } from "../types";
import { Inventory, InventoryFilters } from "../../../../interfaces/Inventory";

export const useInventoryActions = (
  state: InventoryState,
  setState: React.Dispatch<React.SetStateAction<InventoryState>>,
  debouncedQ: string,
) => {
  const navigate = useNavigate();
  const location = useLocation();

  const fetchItems = useCallback(
    async (page = 1) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const filters: InventoryFilters = {
          page,
          limit: state.itemsPerPage,
          q: debouncedQ || undefined,
          partNumber: state.partNumber || undefined,
          category: state.category || undefined,
          condition: state.condition || undefined,
          country: state.country || undefined,
          manufacturer: state.manufacturer || undefined,
          minPrice: state.minPrice > 0 ? state.minPrice : undefined,
          maxPrice: state.maxPrice > 0 ? state.maxPrice : undefined,
          sortBy: state.sort === "newest" ? "createdAt" : "price",
          sortOrder: state.sort === "price_asc" ? "asc" : "desc",
        };

        const response = await getInventoryItems(filters);
        const data = response.data;
        setState((prev) => ({
          ...prev,
          allInventoryItems: data.items,
          total: data.total,
          pages: data.pages,
          page: data.page,
          loading: false,
        }));
      } catch (error: any) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: getErrorMessage(error, "We couldn't load your inventory. Please refresh the page"),
        }));
      }
    },
    [debouncedQ, state.itemsPerPage, state.partNumber, state.category, state.condition, state.country, state.manufacturer, state.minPrice, state.maxPrice, state.sort, setState],
  );

  const handleFilterChange = (key: keyof InventoryState, value: any) => {
    setState((prev) => ({ ...prev, [key]: value, page: 1 }));

    if (key === "q") {
      const newSearchParams = new URLSearchParams(location.search);
      if (value && value.trim()) {
        newSearchParams.set("q", value.trim());
      } else {
        newSearchParams.delete("q");
      }
      const newSearch = newSearchParams.toString();
      const newPath = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      navigate(newPath, { replace: true });
    }
  };

  const handleClearFilters = () => {
    setState((prev) => ({
      ...prev,
      q: "",
      filterKeyword: "",
      partNumber: "",
      category: "",
      condition: "",
      country: "",
      manufacturer: "",
      minPrice: 0,
      maxPrice: 0,
      page: 1,
    }));
    // [2026-02-12] keyword-search-fix: Clear URL search param too
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete("q");
    const newSearch = newSearchParams.toString();
    const newPath = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    navigate(newPath, { replace: true });
  };

  const handleDeleteConfirm = async (
    item: Inventory,
    setIsDeleting: (id: string | null) => void,
    setDeleteConfirmItem: (item: Inventory | null) => void,
  ) => {
    if (!item._id) return;
    try {
      setIsDeleting(item._id);
      await deleteInventoryItem(item._id);
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.success("Item deleted successfully");
      setState((prev) => ({
        ...prev,
        items: prev.items.filter((i) => i._id !== item._id),
        total: Math.max(0, prev.total - 1),
      }));
      setDeleteConfirmItem(null);
    } catch (e: any) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(e, "We couldn't delete this item. Please try again");
      setState((prev) => ({ ...prev, page: 1 }));
    } finally {
      setIsDeleting(null);
    }
  };

  return { fetchItems, handleFilterChange, handleClearFilters, handleDeleteConfirm };
};
